import React, { KeyboardEvent } from 'react';
import { Dayjs } from 'dayjs';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { FormHelperText, InputLabel, useTheme } from '@mui/material';
import { PickerStateProps } from '@mui/x-date-pickers/internals';

export interface DatePickerProps {
  label?: string;
  inputFormat?: string;
  value?: Dayjs | null;
  handleChange: PickerStateProps<Dayjs, Dayjs>['onChange'];
  required?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  error?: string;
  disabled?: boolean;
}

function DatePicker(props: DatePickerProps): JSX.Element {
  const { label, inputFormat, value, handleChange, minDate, maxDate, error } = props;
  const theme = useTheme();

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Prevent all keyboard input including Backspace
    e.preventDefault();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {label && (
        <InputLabel
          className="sb-textfield-label"
          required={props.required}
          sx={{
            mb: '8px',
            color: error ? theme.palette.error.main : theme.palette.common.black,
            fontSize: '14px',
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: '0.3px',
            '&.Mui-required .MuiFormLabel-asterisk': {
              color: 'inherit',
              marginLeft: '4px',
            },
          }}
        >
          {label || 'Date'}
        </InputLabel>
      )}
      <DesktopDatePicker
        className="w-full date-picker"
        inputFormat={inputFormat || 'DD/MM/YYYY'}
        disableMaskedInput={true} // Disable masked input
        value={value}
        onChange={handleChange}
        renderInput={params => (
          <TextField
            {...params}
            error={!!error}
            onKeyDown={onKeyDown}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: 'white',

                '& fieldset': {
                  borderColor: 'rgba(0,0,0,0.12)',
                },

                '&:hover fieldset': {
                  borderColor: 'rgba(0,0,0,0.2)',
                },

                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },

                // Disabled state
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(245, 245, 245, 0.5)',
                  backdropFilter: 'blur(8px)',
                  cursor: 'not-allowed',

                  '& .MuiOutlinedInput-input': {
                    color: 'rgba(26, 26, 26, 0.5)',
                    WebkitTextFillColor: 'rgba(26, 26, 26, 0.5)',
                    cursor: 'not-allowed',
                  },
                },
              },
            }}
            inputProps={{
              ...params.inputProps,
              readOnly: true,
              style: {
                cursor: 'not-allowed',
              },
            }}
          />
        )}
        minDate={minDate}
        maxDate={maxDate}
        disabled={props.disabled}
      />
      {error && (
        <FormHelperText error className="m-0">
          {error}
        </FormHelperText>
      )}
    </LocalizationProvider>
  );
}

export default DatePicker;
